/** @format */

import React, { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'
import { _$, dayjs, Icon } from '@king-design/react'
import SessionIcon from 'src/assets/icon/session'
import { SessionDetailSessionTrace } from './typescript'
import { secondsFormat } from 'src/utils/common'
import { FormatCompactK } from 'src/components'
import variable from 'src/static/styles/var'

const IO_LINE_HEIGHT = 23

/** 内容含换行符时可展开/收起 */
const isIoExpandable = (text?: string) => Boolean(text?.includes('\n'))

interface IoSectionProps {
  label: string
  text?: string
  expanded: boolean
  onToggle: () => void
}

const IoSection: React.FC<IoSectionProps> = ({ label, text, expanded, onToggle }) => {
  const displayText = text || '-'
  const expandable = isIoExpandable(text)
  const togglingRef = useRef(false)

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (togglingRef.current) {
        return
      }
      togglingRef.current = true
      onToggle()
      requestAnimationFrame(() => {
        togglingRef.current = false
      })
    },
    [onToggle]
  )

  return (
    <section className="span-item__io">
      <div
        className={['span-item__io-row', expanded ? 'is-expanded' : ''].filter(Boolean).join(' ')}
      >
        <div className="span-item__io-label">
          <SessionIcon width={16} height={14} aria-hidden />
          <span>{label}</span>
        </div>
        {expandable ? (
          <>
            <div className="span-item__io-main">
              {!expanded ? <div className="span-item__io-preview">{displayText}</div> : null}
              <div
                className={`span-item__io-content-wrap ${expanded ? 'is-expanded' : ''}`}
                aria-hidden={!expanded}
              >
                <div className="span-item__io-content-inner">
                  <div className="span-item__io-content">{displayText}</div>
                </div>
              </div>
            </div>
            <a
              className={`span-item__io-chevron ${expandable ? 'is-expandable' : ''}`}
              onClick={expandable ? handleToggle : undefined}
            >
              <Icon className="ion-chevron-down" size={12} aria-hidden />
            </a>
          </>
        ) : (
          <div className="span-item__io-content">{displayText}</div>
        )}
      </div>
    </section>
  )
}

export interface SpanItemProps {
  className?: string
  trace: SessionDetailSessionTrace
  expand: boolean
  toTraceDetail: (trace: { TraceId: string; StartTime: string }) => void
  /** 时间轴锚点选中时高亮 */
  active?: boolean
  index: number
  onExpandChange: () => void
}

const SpanItem: React.FC<SpanItemProps> = ({
  className,
  index,
  trace,
  toTraceDetail,
  expand,
  active,
  onExpandChange
}) => {
  const [inputExpand, setInputExpand] = useState(false)
  const [outputExpand, setOutputExpand] = useState(false)

  const handleHeaderClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onExpandChange()
    },
    [onExpandChange]
  )

  return (
    <article
      className={[className, active ? 'span-item--active' : ''].filter(Boolean).join(' ')}
      data-trace-id={trace?.TraceId}
    >
      <header className="span-item__header">
        <button type="button" className="span-item__header-btn" onClick={handleHeaderClick}>
          <span className="span-item__start">
            <i>#{index + 1}.</i>
            <Icon className="k-icon-time" />
            {_$('开始时间')}：{dayjs(trace?.StartTime).format('YYYY-MM-DD HH:mm:ss')}
          </span>
          <Icon
            className={`ion-chevron-down span-item__chevron ${expand ? 'is-expanded' : ''}`}
            size={14}
            aria-hidden
          />
        </button>
      </header>

      <div className={`span-item__body-wrap ${expand ? 'is-expanded' : ''}`} aria-hidden={!expand}>
        <div className="span-item__body-inner">
          <div className="span-item__body">
            <IoSection
              label="Input"
              text={trace?.Input}
              expanded={inputExpand}
              onToggle={() => setInputExpand(prev => !prev)}
            />
            <IoSection
              label="Output"
              text={trace?.Output}
              expanded={outputExpand}
              onToggle={() => setOutputExpand(prev => !prev)}
            />

            <footer className="span-item__meta">
              <span className="span-item__meta-item">
                <span className="span-item__meta-label">{_$('TraceID')}：</span>
                <a
                  className="span-item__meta-link"
                  onClick={e => {
                    e.preventDefault()
                    toTraceDetail({
                      TraceId: trace?.TraceId,
                      StartTime: trace?.StartTime
                    })
                  }}
                >
                  {trace?.TraceId}
                </a>
              </span>
              <span className="span-item__meta-item">
                <span className="span-item__meta-label">{_$('耗时')}：</span>
                <span className="span-item__meta-value">{secondsFormat(trace?.DurationMs)}</span>
              </span>
              <span className="span-item__meta-item">
                <span className="span-item__meta-label">Total Tokens：</span>
                <span className="span-item__meta-value">
                  <FormatCompactK value={trace?.TotalTokens} />
                </span>
              </span>
            </footer>
          </div>
        </div>
      </div>
    </article>
  )
}

export default styled(SpanItem)`
  border-radius: 8px;
  box-sizing: border-box;
  margin-bottom: 16px;
  background: #fefefe;
  border: 1px solid ${variable.borderColor};
  scroll-margin-top: 80px;

  &.span-item--active {
    border-color: ${variable.primaryColor};
  }

  .span-item__header {
    border-bottom: 1px solid ${variable.borderColor};
  }
  .span-item__header-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 12px 16px;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
    font: inherit;
    color: rgba(0, 0, 0, 0.85);

    &:hover {
      background: rgba(0, 0, 0, 0.02);
    }
  }

  .span-item__start {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    line-height: 22px;
  }

  .span-item__chevron,
  .span-item__io-chevron {
    flex-shrink: 0;
    transition: transform 0.28s ease;
  }

  .span-item__chevron.is-expanded,
  .span-item__io-row.is-expanded .span-item__io-chevron {
    transform: rotate(180deg);
  }

  .span-item__body-wrap {
    display: grid;
    grid-template-rows: 0fr;
    overflow: hidden;
    transition: grid-template-rows 0.28s ease, opacity 0.22s ease;

    &:not(.is-expanded) {
      opacity: 0;
      pointer-events: none;
    }

    &.is-expanded {
      grid-template-rows: 1fr;
      opacity: 1;
    }
  }

  .span-item__io-content-wrap {
    display: grid;
    grid-template-rows: 0fr;
    overflow: hidden;
    transition: grid-template-rows 0.28s ease, opacity 0.22s ease;

    &:not(.is-expanded) {
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
    }

    &.is-expanded {
      grid-template-rows: 1fr;
      opacity: 1;
      visibility: visible;
    }
  }

  .span-item__body-inner,
  .span-item__io-content-inner {
    min-height: 0;
    overflow: hidden;
  }

  .span-item__body {
    padding: 12px 16px 16px;
  }

  @media (prefers-reduced-motion: reduce) {
    .span-item__body-wrap,
    .span-item__io-content-wrap {
      transition: none;
    }

    .span-item__chevron,
    .span-item__io-chevron {
      transition: none;
    }
  }

  .span-item__io {
    margin-bottom: 12px;
    border: 1px solid rgba(0, 0, 0, 0.04);
    border-radius: 4px;
    background: #fff;
    overflow: hidden;

    &:last-of-type {
      margin-bottom: 16px;
    }
  }

  .span-item__io-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-height: 40px;
    padding: 10px 12px;
    background: transparent;

    .span-item__io-main {
      flex: 1;
      min-width: 0;
    }

    .span-item__io-label {
      display: inline-flex;
      flex-shrink: 0;
      align-items: center;
      gap: 8px;
      padding-top: 1px;
      font-size: 14px;
      font-weight: 600;
      line-height: 22px;
      color: rgba(0, 0, 0, 0.85);

      .is-expandable {
        cursor: pointer;
      }
    }

    .span-item__io-preview {
      flex: 1;
      min-width: 0;
      font-size: 14px;
      line-height: ${IO_LINE_HEIGHT}px;
      color: rgba(0, 0, 0, 0.85);
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 1;
      word-break: break-word;
    }

    .span-item__io-content {
      flex: 1;
      min-width: 0;
      font-size: 14px;
      line-height: ${IO_LINE_HEIGHT}px;
      color: rgba(0, 0, 0, 0.85);
      white-space: pre-wrap;
      word-break: break-word;
    }

    .span-item__io-chevron {
      line-height: ${IO_LINE_HEIGHT}px;
    }
  }

  .span-item__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 24px;
    padding-top: 4px;
    font-size: 12px;
    line-height: 20px;
  }

  .span-item__meta-item {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0;
    min-width: 0;
  }

  .span-item__meta-label {
    flex-shrink: 0;
  }

  .span-item__meta-value {
    word-break: break-all;
  }

  .span-item__meta-link {
    color: ${variable.primaryColor};
    text-decoration: none;
    word-break: break-all;

    &:hover {
      text-decoration: underline;
    }
  }
`
